import * as React from "react";
import {
  RiDownloadLine,
  RiImageLine,
  RiErrorWarningLine,
} from "@remixicon/react";
import { cn } from "../../../lib/utils";
import { SystemTooltip } from "../SystemTooltip";

export interface ImageTileProps {
  /** Image source URL */
  src?: string;
  /** Alt text for the image */
  alt?: string;
  /** Show loading skeleton */
  isLoading?: boolean;
  /** Show error state */
  hasError?: boolean;
  /** Callback when download button is clicked */
  onDownload?: () => void;
  /** Callback when tile is clicked */
  onClick?: () => void;
  /** Additional class name */
  className?: string;
  /** Country label to show in corner */
  countryLabel?: string;
}

/**
 * ImageTile - A tile component for displaying generated images
 *
 * Features:
 * - Default state: Image with border-radius, subtle border
 * - Hover state: Overlay with download button
 * - Loading state: Skeleton with shimmer animation
 * - Error state: Error icon with message
 * - Uses capsule-preset design tokens
 *
 * @example
 * ```tsx
 * <ImageTile
 *   src="https://example.com/image.jpg"
 *   alt="Generated image"
 *   onDownload={() => handleDownload()}
 *   onClick={() => openPreview()}
 * />
 * ```
 */
function ImageTile({
  src,
  alt = "Image",
  isLoading = false,
  hasError = false,
  onDownload,
  onClick,
  className,
  countryLabel,
}: ImageTileProps) {
  const [imageLoaded, setImageLoaded] = React.useState(false);

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload?.();
  };

  // Loading state - skeleton with shimmer animation
  if (isLoading) {
    return (
      <div
        className={cn(
          "relative flex flex-col overflow-hidden rounded-2xl",
          "border border-solid border-stroke-secondary bg-bg-primary",
          className,
        )}
      >
        {/* Skeleton image area */}
        <div className="relative aspect-square w-full overflow-hidden bg-[#e4e4e7]">
          {/* Shimmer animation overlay */}
          <div
            className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite]"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(248,252,255,0.6) 50%, transparent 100%)",
            }}
          />
        </div>
        {/* Loading text footer */}
        <div className="flex h-8 items-center px-3">
          <span className="text-xs font-medium leading-[18px] text-text-subtle">
            Image loading . . .
          </span>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div
        className={cn(
          "relative flex aspect-square flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl",
          "border border-solid border-stroke-critical bg-bg-secondary",
          className,
        )}
      >
        <RiErrorWarningLine className="size-8 text-icon-critical" />
        <span className="text-xs text-text-critical">Failed to load</span>
      </div>
    );
  }

  // Empty state (no image yet) - placeholder with icon
  if (!src) {
    return (
      <div
        className={cn(
          "relative flex flex-col overflow-hidden rounded-2xl",
          "border border-solid border-stroke-secondary bg-bg-primary",
          className,
        )}
      >
        {/* Placeholder image area */}
        <div className="flex aspect-square w-full items-center justify-center bg-[#e6f4ff]">
          <RiImageLine className="size-6 text-icon-disabled" />
        </div>
      </div>
    );
  }

  // Image loaded state
  return (
    <div
      className={cn(
        "group relative flex flex-col cursor-pointer overflow-hidden rounded-2xl",
        "border border-solid border-stroke-secondary bg-bg-primary",
        "transition-all duration-200 hover:border-stroke-primary hover:shadow-md",
        className,
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      aria-label={`View ${alt}`}
    >
      {/* Image */}
      <div className="relative aspect-square w-full overflow-hidden">
        <img
          src={src}
          alt={alt}
          className={cn(
            "size-full object-cover transition-transform duration-200",
            !imageLoaded && "opacity-0",
            "group-hover:scale-105",
          )}
          onLoad={() => setImageLoaded(true)}
        />

        {/* Loading placeholder while image loads */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-[#e4e4e7]">
            <div
              className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite]"
              style={{
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(248,252,255,0.6) 50%, transparent 100%)",
              }}
            />
          </div>
        )}

        {/* Hover overlay - semi-transparent dark background */}
        <div
          className={cn(
            "absolute inset-0 rounded-t-2xl",
            "bg-[rgba(15,23,42,0.4)]",
            "opacity-0 transition-opacity duration-200 group-hover:opacity-100",
          )}
        >
          {/* Expand Image label - top center */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2">
            <span className="rounded bg-black px-2 py-[1px] text-xs leading-4 text-white whitespace-nowrap shadow-[0px_4px_8px_rgba(2,0,94,0.1),0px_2px_4px_rgba(2,0,94,0.06)]">
              Expand Image
            </span>
          </div>

          {/* Download button - bottom center */}
          {onDownload && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
              <SystemTooltip content="Download" side="top" sideOffset={8}>
                <button
                  type="button"
                  onClick={handleDownloadClick}
                  className={cn(
                    "flex items-center justify-center rounded-full p-2",
                    "bg-white text-icon-primary",
                    "transition-colors hover:bg-bg-hover",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-stroke-blue",
                  )}
                  aria-label={`Download ${alt}`}
                >
                  <RiDownloadLine className="size-5" />
                </button>
              </SystemTooltip>
            </div>
          )}

          {/* Country label - bottom left */}
          {countryLabel && (
            <div className="absolute bottom-4 left-4 flex items-center gap-2">
              <span className="rounded bg-bg-primary/90 px-2 py-1 text-xs font-medium text-text-primary">
                {countryLabel}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ImageTile.displayName = "ImageTile";

export { ImageTile };
